import { alpha, Button, type ButtonProps, useTheme } from "@mui/material";
import type { FC } from "react";

type FormSubmitButtonVariant = "primary" | "realization";

type Props = Omit<ButtonProps, "type"> & {
	type?: "button" | "submit" | "reset";
	loading?: boolean;
	variant?: FormSubmitButtonVariant;
};

const VARIANT_SHADOW: Record<
	FormSubmitButtonVariant,
	(color: string) => string
> = {
	primary: (color) => `0 4px 14px ${alpha(color, 0.3)}`,
	realization: (color) => `0 6px 16px -4px ${alpha(color, 0.4)}`,
};

export const FormSubmitButton: FC<Props> = ({
	children,
	type = "submit",
	disabled,
	loading,
	variant = "primary",
	sx,
	...rest
}) => {
	const theme = useTheme();
	const isDisabled = disabled || loading;
	const bgColor = theme.palette[variant].main;
	const shadowColor = VARIANT_SHADOW[variant](bgColor);

	return (
		<Button
			type={type}
			disabled={disabled}
			loading={loading}
			disableElevation
			disableRipple
			sx={{
				width: "100%",
				py: 1.75,
				fontWeight: 600,
				fontSize: 16,
				borderRadius: "16px",
				textTransform: "none",
				color: disabled ? theme.palette.grey[400] : theme.palette.common.white,
				bgcolor: bgColor,
				boxShadow: isDisabled ? "none" : shadowColor,
				cursor: disabled ? "not-allowed" : "pointer",
				transition: "transform 120ms ease-out",
				"&:active": isDisabled ? undefined : { transform: "scale(0.97)" },
				"&.Mui-disabled": {
					color: loading
						? theme.palette.common.white
						: theme.palette.deprecated.grey[400],
					bgcolor: loading ? bgColor : theme.palette.deprecated.grey[100],
				},
				"&.MuiButton-loading": {
					color: "transparent",
					bgcolor: theme.palette.primary.main,
				},
				".MuiButton-loadingIndicator": {
					color: theme.palette.common.white,
				},
				...sx,
			}}
			{...rest}
		>
			{children}
		</Button>
	);
};
